import React, { useRef, useEffect, useState } from "react";
import { RCCDropDownItem, RCCGrid, RCCIcon } from "react-common-components";
import OptionsColumn from "src/components/DefaultColumnsList/Options";
import { useTranslation } from "src/context/Translations/context";
import useEffectSkipInitialRender from "src/hooks/useEffectSkipInitialRender";
import CheckPermission from "src/components/CheckPermission";
import { PermissionEnum } from "src/context/Auth/interface";
import { useAuth } from "src/context/Auth/context";
import { checkSituationOccurrence, IListOfOccurrences, IOccurrence, IRefModalAddOrChange, IRefModalAttachments, IRefModalDelete, IRefModalFcaAnalysis, IRefModalHistoric, IRefModalImmediateActions, IRefModalRegisterHistory } from "../interface";
import CardOccurences from "./CardOccurences";
import ListOccurrences from "./ListOccurrences";
import ModalAddOrChange from "./ModalAddOrChange";
import ModalRegisterHistoric from "./modalRegisterHistory ";
import ModalImmediateActions from "./modalImmediateActions";
import ModalDelete from "./modalDelete";
import ModalAttachments from "./modalAttachments";
import ModalFcaAnalysis from "./modalFcaAnalysis";
import ModalHistoric from "./modalHistoric";

const ListOfOccurences: React.FC<IListOfOccurrences> = ({
	layoutList, 
	data,
	filterList,
	onCallbackDelete,
	onCallbackUpdate,
	loadGroupers
}) => {
	const {translate} = useTranslation();
	const { administrator, superUser, user, checkIfHasSpecificPermission } = useAuth();

	const refModalRegisterHistory = useRef<IRefModalRegisterHistory>(null);
	const refModalImmediateActions = useRef<IRefModalImmediateActions>(null);
	const refModalAddOrChange = useRef<IRefModalAddOrChange>(null);
	const refModalDelete = useRef<IRefModalDelete>(null);
	const refAttachments = useRef<IRefModalAttachments>(null);
	const refModalFcaAnalysis = useRef<IRefModalFcaAnalysis>(null);
	const refModalHistoric = useRef<IRefModalHistoric>(null);

	const [occurrences, setOccurrences] = useState<IOccurrence[]>(data);
	const [dataFiltered, setDataFiltered] = useState<IOccurrence[]>(occurrences);

	const permisionToCreateUpdate = checkIfHasSpecificPermission("/occurrences/panel", PermissionEnum.CREATE_UPDATE);
	const permisionToCreateUpdateDelete = checkIfHasSpecificPermission("/occurrences/panel", PermissionEnum.CREATE_UPDATE_DELETE);

	const options = (row: IOccurrence) => {

		const statusLateOrOpendOrNotstarted = row.ocb001Status === checkSituationOccurrence.LATE || row.ocb001Status === checkSituationOccurrence.OPENED || row.ocb001Status === checkSituationOccurrence.NOT_STARTED;
		const isAdminOrSuper = administrator || superUser;
		const operatorOrExecutorOrAutorityOrResponsible = row.ocb001Efa005Id === user.efa005Id || row.ocb001Efa005Id2 === user.efa005Id || row.ocb001Efa005Id3 === user.efa005Id || row.ocb001Efa005Id4 === user.efa005Id;
		const canRecordHistory = administrator || superUser || row.ocb001Efa005Id2 === user.efa005Id;

		const optionVisualize = () => (
			<>
				<RCCDropDownItem
					selfEnclose
					onClick={() => {refModalAddOrChange.current?.actionButtonChange(row.ocb001Id, false, true);}}
				>
					<RCCIcon name="eye"/>
							  {translate("Visualizar")}
				</RCCDropDownItem>
			</>
		);

		const optionFCA = () => (
			<>
				<RCCDropDownItem 
					selfEnclose
					onClick={() => { refModalFcaAnalysis.current?.actionButtonFcaAnalysis(row); }}
				>
					<RCCIcon name="exclamationCircle" 
					/>
					{`${translate("Análises críticas")} (FCA)`}
				</RCCDropDownItem>
			</>
		);

		return (	
			<RCCGrid 
				container
				item
				styleInline="justify-content: flex-end;"
			>
				<OptionsColumn>

					{
						isAdminOrSuper || (operatorOrExecutorOrAutorityOrResponsible && permisionToCreateUpdate) ? 
							<>
								{row.ocb001Status === checkSituationOccurrence.FINISHED || row.ocb001Status === checkSituationOccurrence.CANCELED || row.ocb001Status === checkSituationOccurrence.DISAPPROVED ? (
									<>
										{optionVisualize()}
									</>
								) : (
									<RCCDropDownItem
										selfEnclose
										onClick={() => {refModalAddOrChange.current?.actionButtonChange(row.ocb001Id, false);}}
									>
										<RCCIcon name="pencil"/>
								  				{translate("Alterar")}
									</RCCDropDownItem>
								)}
				
					
								{(row.ocb001Status !== checkSituationOccurrence.FINISHED && row.ocb001Status !== checkSituationOccurrence.CANCELED && row.ocb001Status !== checkSituationOccurrence.DISAPPROVED) && (
									<RCCDropDownItem 
										selfEnclose
										onClick={() => { refModalAddOrChange.current?.actionButtonChange(row.ocb001Id, true); }}
									>
										<RCCIcon name="copy" 
										/>
										{translate("Copiar")}
									</RCCDropDownItem>
								)}
	
								{statusLateOrOpendOrNotstarted && canRecordHistory && (
									<RCCDropDownItem 
										selfEnclose
										onClick={() => { refModalRegisterHistory.current?.actionButtonRegisterHistory(row); }}
									>
										<RCCIcon name="checkCircle" 
										/>
										{translate("Registrar histórico")}
									</RCCDropDownItem>		
								)}

								{optionFCA()}
							</>

							:

							<>
								{optionVisualize()}
								{optionFCA()}
							</>
					}

					<CheckPermission minimumPermissionRequired={PermissionEnum.CREATE_UPDATE_DELETE}>
						<RCCDropDownItem 
							selfEnclose
							onClick={() => { refModalDelete.current?.actionButtonDelete(row); }}
						>
							<RCCIcon name="trash" 
							/>
							{translate("Excluir")}
						</RCCDropDownItem>														
					</CheckPermission>		  
		  		</OptionsColumn>
			</RCCGrid>
		);
	};

	useEffect(() => {
		if(!filterList){
			setDataFiltered(occurrences);
			return;
		}	
		if(filterList === checkSituationOccurrence.FINISHED || filterList === checkSituationOccurrence.CANCELED || filterList === checkSituationOccurrence.DISAPPROVED){
			setDataFiltered(occurrences.filter((occurrence: any) => occurrence.ocb001Status === filterList));
			return;
		}
		if(filterList === checkSituationOccurrence.LATE){
			setDataFiltered(occurrences.filter((occurrence: any) => occurrence.late === true && occurrence.ocb001Status !== checkSituationOccurrence.FINISHED && occurrence.ocb001Status !== checkSituationOccurrence.CANCELED && occurrence.ocb001Status !== checkSituationOccurrence.DISAPPROVED));
			return;
		}
		setDataFiltered(occurrences.filter((occurrence: any) => occurrence.ocb001Status === filterList && occurrence.late === false));
	}, [filterList, occurrences]);

	useEffectSkipInitialRender(() => {
		setOccurrences(data);
	}, [data]);

	return (
		<>

			{layoutList === "card" ? (
				<CardOccurences
					data={dataFiltered}
					options={options}
					refModalRegisterHistory={refModalRegisterHistory}
					openModalHistory={(row:  IOccurrence) => {
						if(!refModalHistoric.current)
							return;
						refModalHistoric.current.actionButtonHistoric(row);
					}}	
					openModalImmediateActions={(row:  IOccurrence) => {
						if(!refModalImmediateActions.current)
							return;
						refModalImmediateActions.current.actionButtonImmediateActions(row, false, () => {
							if(onCallbackUpdate)
								onCallbackUpdate(row.ocb001Id);
						});
					}}	 
					openModalAttachments={(row:  IOccurrence) => {
						if(!refAttachments.current)
							return;
																	
						refAttachments.current.actionButtonAttachments(row, () => {
							if(onCallbackUpdate)
								onCallbackUpdate(row.ocb001Id);
						}, !permisionToCreateUpdateDelete);
					}}
				/>
			): (
				<ListOccurrences 
					data={dataFiltered}
					options={options}
					refModalRegisterHistory={refModalRegisterHistory}
					openModalHistory={(row:  IOccurrence) => {
						if(!refModalHistoric.current)
							return;
						refModalHistoric.current.actionButtonHistoric(row);
					}}	
					openModalImmediateActions={(row:  IOccurrence) => {
						if(!refModalImmediateActions .current)
							return;
						refModalImmediateActions.current.actionButtonImmediateActions(row, false, () => {
							if(onCallbackUpdate)
								onCallbackUpdate(row.ocb001Id);
						});
					}}	 
					openModalAttachments={(row:  IOccurrence) => {
						if(!refAttachments.current)
							return;
																	
						refAttachments.current.actionButtonAttachments(row, () => {
							if(onCallbackUpdate)
								onCallbackUpdate(row.ocb001Id);
						});
					}}
				/>
			)}

			<ModalAddOrChange ref={refModalAddOrChange} onCallbackUpdate={onCallbackUpdate} loadGroupers={loadGroupers} />
			<ModalRegisterHistoric ref={refModalRegisterHistory} callbackUpdate={onCallbackUpdate}/>
			<ModalImmediateActions ref={refModalImmediateActions}/>
			<ModalDelete ref={refModalDelete} onCallbackDelete={onCallbackDelete} loadGroupers={loadGroupers} />
			<ModalAttachments ref={refAttachments} />
			<ModalFcaAnalysis ref={refModalFcaAnalysis} />
			<ModalHistoric ref={refModalHistoric} />
		</>
	);
};

export default ListOfOccurences;
